import { useState } from "react";
import { ModuleAccessGuard } from "@/components/ModuleAccessGuard";
import { DataTable } from "@/components/shared/DataTable";
import { FilterBar } from "@/components/shared/FilterBar";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { useEntities, useCreateEntity, useUpdateEntity, useDeleteEntity } from "@/hooks/useOnboarding";
import { useToast } from "@/hooks/use-toast";
import { Tag, Plus, Edit, Trash2, MoreHorizontal, FileText, CreditCard, Building, Zap, Copy } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import type { Column } from "@/components/shared/DataTable";

export default function Entities() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState<Record<string, any>>({});
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingEntity, setEditingEntity] = useState<any>(null);
  const { toast } = useToast();

  const offset = (page - 1) * 20;
  const { data: entitiesData, isLoading } = useEntities({ offset, limit: 20, search, ...filters });
  const createEntity = useCreateEntity();
  const updateEntity = useUpdateEntity();
  const deleteEntity = useDeleteEntity();

  const [formData, setFormData] = useState({
    customer_id: "",
    site_id: "",
    account_id: "",
    identifier: "",
    identifier_type: "GST",
    address: "",
    state: "",
    postal_code: "",
    status: "active"
  });

  const handleSubmit = async () => {
    try {
      if (editingEntity) {
        await updateEntity.mutateAsync({ entityId: editingEntity.id, updates: formData });
        toast({ title: "Entity updated successfully" });
      } else {
        await createEntity.mutateAsync(formData);
        toast({ title: "Entity created successfully" });
      }
      setIsCreateOpen(false);
      setEditingEntity(null);
      resetForm();
    } catch (error) {
      toast({ title: "Error", description: String(error), variant: "destructive" });
    }
  };

  const resetForm = () => {
    setFormData({
      customer_id: "", site_id: "", account_id: "", identifier: "",
      identifier_type: "GST", address: "", state: "", postal_code: "", status: "active"
    });
  };

  const getEntityIcon = (type: string) => {
    switch (type) {
      case "GST": return <FileText className="h-4 w-4" />;
      case "VAT": return <CreditCard className="h-4 w-4" />;
      case "TenantID": return <Building className="h-4 w-4" />;
      case "MeterID": return <Zap className="h-4 w-4" />;
      default: return <Tag className="h-4 w-4" />;
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({ title: "Copied to clipboard" });
  };

  const columns: Column<any>[] = [
    { key: "customer_name", header: "Customer", render: (item) => item.customer_name },
    { key: "site_name", header: "Site", render: (item) => item.site_name },
    { key: "account_number", header: "Account", render: (item) => item.account_number },
    {
      key: "identifier",
      header: "Identifier",
      render: (item) => (
        <div className="flex items-center gap-2">
          <span>{item.identifier}</span>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={() => copyToClipboard(item.identifier)}
          >
            <Copy className="h-3 w-3" />
          </Button>
        </div>
      )
    },
    {
      key: "identifier_type",
      header: "Entity Type",
      render: (item) => (
        <Badge variant="secondary" className="flex items-center gap-1 w-fit">
          {getEntityIcon(item.identifier_type)}
          {item.identifier_type}
        </Badge>
      )
    },
    { key: "address", header: "Address", render: (item) => item.address },
    { key: "state", header: "State", render: (item) => item.state },
    { key: "postal_code", header: "Postal Code", render: (item) => item.postal_code },
    {
      key: "status",
      header: "Status",
      render: (item) => {
        const status = item.status;
        const variant = status === "active" ? "default" : "secondary";
        return <Badge variant={variant}>{status}</Badge>;
      }
    },
    {
      key: "actions",
      header: "Actions",
      render: (item) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon"><MoreHorizontal className="h-4 w-4" /></Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => { setEditingEntity(item); setIsCreateOpen(true); }}>
              <Edit className="mr-2 h-4 w-4" />Edit
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem className="text-destructive" onClick={() => deleteEntity.mutate(item.id)}>
              <Trash2 className="mr-2 h-4 w-4" />Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )
    }
  ];

  const filterConfig = [
    { key: "customer_name", label: "Customer", type: "select" as const, options: [] },
    { key: "site_name", label: "Site", type: "select" as const, options: [] },
    { key: "account_number", label: "Account", type: "select" as const, options: [] }
  ];

  return (
    <ModuleAccessGuard moduleId="onboarding">
      <div className="space-y-6">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Entities</h1>
            <p className="text-muted-foreground mt-2">Manage entity identifiers (GST, VAT, Tenant ID, Meter ID).</p>
          </div>
          <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
            <DialogTrigger asChild>
              <Button variant="hero" onClick={resetForm}><Plus className="h-4 w-4 mr-2" />Create Entity</Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>{editingEntity ? "Edit Entity" : "Create New Entity"}</DialogTitle>
              </DialogHeader>
              <Tabs defaultValue="association" className="w-full">
                <TabsList className="grid w-full grid-cols-4">
                  <TabsTrigger value="association">Association</TabsTrigger>
                  <TabsTrigger value="identifier">Identifier</TabsTrigger>
                  <TabsTrigger value="address">Address</TabsTrigger>
                  <TabsTrigger value="status">Status</TabsTrigger>
                </TabsList>
                <TabsContent value="association" className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Customer *</Label>
                      <Input placeholder="Customer ID" value={formData.customer_id} onChange={(e) => setFormData({...formData, customer_id: e.target.value})} />
                    </div>
                    <div className="space-y-2">
                      <Label>Site (Optional)</Label>
                      <Input placeholder="Site ID" value={formData.site_id} onChange={(e) => setFormData({...formData, site_id: e.target.value})} />
                    </div>
                    <div className="space-y-2 col-span-2">
                      <Label>Account (Optional)</Label>
                      <Input placeholder="Account ID" value={formData.account_id} onChange={(e) => setFormData({...formData, account_id: e.target.value})} />
                    </div>
                  </div>
                </TabsContent>
                <TabsContent value="identifier" className="space-y-4">
                  <div className="space-y-2">
                    <Label>Identifier *</Label>
                    <Input value={formData.identifier} onChange={(e) => setFormData({...formData, identifier: e.target.value})} />
                  </div>
                  <div className="space-y-2">
                    <Label>Identifier Type</Label>
                    <Select value={formData.identifier_type} onValueChange={(val) => setFormData({...formData, identifier_type: val})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="GST">GST</SelectItem>
                        <SelectItem value="VAT">VAT</SelectItem>
                        <SelectItem value="TenantID">Tenant ID</SelectItem>
                        <SelectItem value="MeterID">Meter ID</SelectItem>
                        <SelectItem value="Custom">Custom</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </TabsContent>
                <TabsContent value="address" className="space-y-4">
                  <div className="space-y-2">
                    <Label>Address (Optional)</Label>
                    <Textarea value={formData.address} onChange={(e) => setFormData({...formData, address: e.target.value})} />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>State</Label>
                      <Input value={formData.state} onChange={(e) => setFormData({...formData, state: e.target.value})} />
                    </div>
                    <div className="space-y-2">
                      <Label>Postal Code</Label>
                      <Input value={formData.postal_code} onChange={(e) => setFormData({...formData, postal_code: e.target.value})} />
                    </div>
                  </div>
                </TabsContent>
                <TabsContent value="status" className="space-y-4">
                  <div className="space-y-2">
                    <Label>Status</Label>
                    <Select value={formData.status} onValueChange={(val) => setFormData({...formData, status: val})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="inactive">Inactive</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </TabsContent>
              </Tabs>
              <div className="flex justify-end gap-2 mt-4">
                <Button variant="outline" onClick={() => setIsCreateOpen(false)}>Cancel</Button>
                <Button onClick={handleSubmit}>{editingEntity ? "Update" : "Create"}</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="shadow-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Entities</p>
                  <p className="text-2xl font-bold">{entitiesData?.total || 0}</p>
                </div>
                <Tag className="h-5 w-5 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>
          <Card className="shadow-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">GST/VAT</p>
                  <p className="text-2xl font-bold">{entitiesData?.gst_vat || 0}</p>
                </div>
                <FileText className="h-5 w-5 text-success" />
              </div>
            </CardContent>
          </Card>
          <Card className="shadow-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Tenant IDs</p>
                  <p className="text-2xl font-bold">{entitiesData?.tenant_ids || 0}</p>
                </div>
                <Building className="h-5 w-5 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>
          <Card className="shadow-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Meter IDs</p>
                  <p className="text-2xl font-bold">{entitiesData?.meter_ids || 0}</p>
                </div>
                <Zap className="h-5 w-5 text-warning" />
              </div>
            </CardContent>
          </Card>
        </div>

        <FilterBar
          searchPlaceholder="Search entities..."
          searchValue={search}
          onSearchChange={setSearch}
          filters={filterConfig}
          activeFilters={filters}
          onFilterChange={(key, value) => setFilters({...filters, [key]: value})}
          onClearFilters={() => setFilters({})}
        />

        <DataTable
          data={entitiesData?.data || []}
          columns={columns}
          totalCount={entitiesData?.total || 0}
          pageSize={20}
          currentPage={page}
          onPageChange={setPage}
          isLoading={isLoading}
          emptyMessage="No entities found"
          getRowId={(item) => item.id}
        />
      </div>
    </ModuleAccessGuard>
  );
}
